/**
 * Hook useGoogleCalendarConnect
 *
 * Encapsula o início do fluxo OAuth2 do Google Calendar (POST na edge
 * function `calendar-oauth?action=authorize` + abertura do popup).
 *
 * Extraído de `CalendarIntegrationsPanel` para poder ser reutilizado
 * também dentro do modal de criação/edição de calendário
 * (`CalendarFormDialog`), tornando a conexão com o Google visível no
 * lugar onde o usuário realmente cria o calendário.
 *
 * RELEASE:
 *   2026-08-04 v1.0.0 - Versão inicial (extração do handleConnect)
 */

import { useCallback, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';
import { toast } from 'sonner';

const logger = createLogger('useGoogleCalendarConnect');

/** Chave de sessão onde guardamos o correlation_id da tentativa de OAuth. */
export const OAUTH_RID_KEY = 'calendar_oauth_rid';

interface UseGoogleCalendarConnectOptions {
  /** Callback opcional para abrir o diagnóstico quando há correlation_id. */
  onDiagnostics?: (correlationId: string) => void;
}

export function useGoogleCalendarConnect(options?: UseGoogleCalendarConnectOptions) {
  const [isConnecting, setIsConnecting] = useState(false);

  /** Inicia OAuth2 com Google Calendar (POST + popup centralizado). */
  const connect = useCallback(async () => {
    setIsConnecting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        logger.error('Usuário não autenticado para OAuth');
        toast.error('Usuário não autenticado');
        return;
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const redirectUri = window.location.origin + '/tenant/calendar';

      logger.info('Iniciando OAuth Google Calendar');

      const response = await fetch(
        `${supabaseUrl}/functions/v1/calendar-oauth?action=authorize`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
            'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
          body: JSON.stringify({ redirect_uri: redirectUri }),
        },
      );

      const data = await response.json();
      if (!response.ok || !data.auth_url) {
        logger.error('Erro ao obter URL de autorização', data);
        toast.error('Erro ao iniciar conexão com Google', {
          action: data?.correlation_id && options?.onDiagnostics
            ? {
                label: 'Ver diagnóstico',
                onClick: () => options.onDiagnostics?.(data.correlation_id),
              }
            : undefined,
        });
        return;
      }

      // Persiste correlation_id da tentativa para uso pós-redirect
      if (data.correlation_id) {
        try { sessionStorage.setItem(OAUTH_RID_KEY, data.correlation_id); } catch { /* ignora */ }
      }

      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      window.open(
        data.auth_url,
        'google-calendar-oauth',
        `width=${width},height=${height},left=${left},top=${top},menubar=no,toolbar=no`,
      );
    } catch (error) {
      logger.error('Erro ao iniciar OAuth', error);
      toast.error('Erro ao conectar calendário');
    } finally {
      setIsConnecting(false);
    }
  }, [options]);

  return { connect, isConnecting };
}
